import { useState, useEffect, useRef } from "react";
import { 
  MessageSquare, 
  Plus, 
  Trash2, 
  Scale, 
  ScrollText,
  Loader2,
  Edit2,
  Check,
  X
} from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import apiClient from "../lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

type Conversation = {
  id: number;
  search_type: string;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
};

interface ChatHistorySidebarProps {
  activeTab: "olt" | "bylaws";
  currentConversationId: number | null;
  onSelectConversation: (conversationId: number | null) => void;
  onNewConversation: () => void;
}

export default function ChatHistorySidebar({
  activeTab,
  currentConversationId,
  onSelectConversation,
  onNewConversation,
}: ChatHistorySidebarProps) {
  const queryClient = useQueryClient();
  const [searchType, setSearchType] = useState<"olt" | "bylaw">("olt");

  useEffect(() => {
    setSearchType(activeTab === "olt" ? "olt" : "bylaw");
  }, [activeTab]);

  // Fetch conversations
  const { data: conversations = [], isLoading, refetch } = useQuery({
    queryKey: ["records-conversations", searchType],
    queryFn: () => apiClient.recordsConversations.list(searchType),

    // Removed aggressive polling - will refetch manually when needed
    refetchOnWindowFocus: false
  });
  
  // State for editing titles (must be declared before useEffects that use them)
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState<string>("");
  const editInputRef = useRef<HTMLInputElement>(null);

  // Refetch when currentConversationId changes (new conversation created)
  // Use a ref to prevent unnecessary refetches
  const prevConversationIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (currentConversationId && currentConversationId !== prevConversationIdRef.current) {
      prevConversationIdRef.current = currentConversationId;
      refetch();
    }
  }, [currentConversationId, refetch]);

  // Focus input when editing starts
  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  // Delete conversation mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiClient.recordsConversations.delete(id),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["records-conversations"]
      });
      if (currentConversationId) {
        onSelectConversation(null);
      }
    }
  });

  // Update conversation title mutation
  const updateTitleMutation = useMutation({
    mutationFn: ({ id, title }: { id: number; title: string }) => 
      apiClient.recordsConversations.updateTitle(id, title),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["records-conversations"]
      });
    }
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const groupedConversations = conversations.reduce((acc: Record<string, Conversation[]>, conv: Conversation) => {
    const date = new Date(conv.updated_at);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / 86400000);

    let group: string;
    if (diffDays === 0) group = "Today";
    else if (diffDays === 1) group = "Yesterday";
    else if (diffDays < 7) group = "Last Week";
    else group = "Older";

    if (!acc[group]) acc[group] = [];
    acc[group].push(conv);
    return acc;
  }, {} as Record<string, Conversation[]>);

  return (
    <div className="w-64 border-r border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-neutral-200 dark:border-neutral-800">
        <Button
          onClick={onNewConversation}
          className="w-full justify-start gap-2"
          variant="outline"
        >
          <Plus className="h-4 w-4" />
          New Chat
        </Button>
      </div>

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="p-8 text-center text-sm text-neutral-500">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No conversations yet</p>
            <p className="text-xs mt-1">Start a new chat to begin</p>
          </div>
        ) : (
          <div className="p-2">
            {Object.entries(groupedConversations).map(([group, convs]) => (
              <div key={group} className="mb-6">
                <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider px-3 mb-2">
                  {group}
                </h3>
                {(convs as Conversation[]).map((conv) => (
                  <div
                    key={conv.id}
                    className={cn(
                      "group relative flex items-center gap-2 p-2 rounded-lg cursor-pointer mb-1 transition-colors",
                      currentConversationId === conv.id
                        ? "bg-primary/10 text-primary"
                        : "hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
                    )}
                    onClick={() => onSelectConversation(conv.id)}
                  >
                    <div className="flex-shrink-0">
                      {conv.search_type === "olt" ? (
                        <Scale className="h-4 w-4" />
                      ) : (
                        <ScrollText className="h-4 w-4" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      {editingId === conv.id ? (
                        <div className="flex items-center gap-1">
                          <input
                            ref={editInputRef}
                            type="text"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                if (editTitle.trim()) {
                                  updateTitleMutation.mutate({ id: conv.id, title: editTitle.trim() });
                                  setEditingId(null);
                                }
                              } else if (e.key === "Escape") {
                                setEditingId(null);
                                setEditTitle("");
                              }
                            }}
                            className="flex-1 text-sm font-medium bg-white dark:bg-neutral-800 border border-primary rounded px-1 py-0.5"
                            autoFocus
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5"
                            onClick={() => {
                              if (editTitle.trim()) {
                                updateTitleMutation.mutate({ id: conv.id, title: editTitle.trim() });
                                setEditingId(null);
                              }
                            }}
                          >
                            <Check className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5"
                            onClick={() => {
                              setEditingId(null);
                              setEditTitle("");
                            }}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm font-medium truncate">
                            {conv.title}
                          </p>
                          <p className="text-xs text-neutral-500">
                            {formatDate(conv.updated_at)}
                          </p>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingId(conv.id);
                          setEditTitle(conv.title);
                          setTimeout(() => editInputRef.current?.focus(), 0);
                        }}
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm("Delete this conversation?")) {
                            deleteMutation.mutate(conv.id);
                          }
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

